import React, { useState } from 'react';
import './EquipmentProgress.scss';
import { CustomButton, DateTimePicker, Loading } from '../../../../_common';
import { Col, Modal, Row } from 'react-bootstrap';
import {
    GetAssestLoadsByLoadId_getAssestLoadsByLoadId as AssetLoadType, GetAllAssetLoadStatusTypes,
} from '../../../../../types/schemaTypes';
import SearchableDropdown, { DropdownOption } from '../../../../_common/SearchableDropdown/SearchableDropdown';
import { useQuery } from '@apollo/client/react';
import { GET_ALL_ASSET_LOAD_STATUS_TYPES } from '../../../../../graphql/client/queries';
import moment from 'moment';
import { isValidDate } from '../../../../../utils';

interface EditEquipmentProgressModalProps {
    showModal: boolean,
    loading: boolean,
    error: string,
    assetLoad: AssetLoadType;
    onClickCancel: () => void,
    onClickSave: (atOriginIn, atOriginOut, atDestinationIn, atDestinationOut, status) => void,
}

const EditEquipmentProgressModal = ({
    showModal,
    loading,
    error,
    assetLoad,
    onClickCancel,
    onClickSave,
}: EditEquipmentProgressModalProps): React.ReactElement => {
    const [status, setStatus] = useState(assetLoad.status)
    const [originIn, setOriginIn] = useState(assetLoad.atOriginIn)
    const [originOut, setOriginOut] = useState(assetLoad.atOriginOut)
    const [destinationIn, setDestinationIn] = useState(assetLoad.atDestinationIn)
    const [destinationOut, setDestinationOut] = useState(assetLoad.atDestinationOut)

    const [originInError, setOriginInError] = useState('');
    const [originOutError, setOriginOutError] = useState('');
    const [destinationInError, setDestinationInError] = useState('');
    const [destinationOutError, setDestinationOutError] = useState('');

    const dateErrors = {
        invalidError: 'Invalid Date',
        originOutError: 'Date must be greater than Origin In',
        destinationInError: 'Date must be greater than Origin Out',
        destinationOutError: 'Date must be greater than Destination In',
        dateRequiredError: '*Date is required',
    }

    const { data } = useQuery<GetAllAssetLoadStatusTypes>(GET_ALL_ASSET_LOAD_STATUS_TYPES)
    const loadStatusOptions: DropdownOption[] = [];
    if (data && data.getAllAssetLoadStatusTypes) {
        data.getAllAssetLoadStatusTypes.map(status => {
            if (status && !['Created', 'ReadyForDispatch'].includes(status.name))
                loadStatusOptions.push(status);
        })
    }

    const handleOnChangeStatus = (statusId: number) => {
        let status = ''
        data?.getAllAssetLoadStatusTypes?.map(item => {
            if (item?.id === statusId)
                status = item.name;
        })
        setStatus(status);
    }

    const handleDateInputChange = (type, value) => {
        if (type === "originIn") {
            setOriginIn(value);
            setOriginInError('');
        }
        if (type === "originOut") {
            setOriginOut(value);
            setOriginOutError('');
        }
        if (type === "destinationIn") {
            setDestinationIn(value);
            setDestinationInError('');
        }
        if (type === "destinationOut") {
            setDestinationOut(value);
            setDestinationOutError('');
        }
    }

    const handleOnClickSave = () => {
        onClickSave(originIn, originOut, destinationIn, destinationOut, status);
    }

    const isSaveDisabled = () => {
        let isError = false;

        if (originIn && !isValidDate(new Date(originIn))) {
            originInError === '' && setOriginInError(dateErrors.invalidError);
            isError = true;
        } else {
            originInError !== '' && setOriginInError('');
        }

        if (originOut && !isValidDate(new Date(originOut))) {
            originOutError === '' && setOriginOutError(dateErrors.invalidError);
            isError = true;
        } else if (originOut && originIn && isValidDate(new Date(originIn)) && (moment(originOut) < moment(originIn))) {
            originOutError === '' && setOriginOutError(dateErrors.originOutError);
            isError = true;
        } else {
            originOutError !== '' && setOriginOutError('');
        }

        if (destinationIn && !isValidDate(new Date(destinationIn))) {
            destinationInError === '' && setDestinationInError(dateErrors.invalidError);
            isError = true;
        } else if (destinationIn && originOut && isValidDate(new Date(originOut)) && (moment(destinationIn) < moment(originOut))) {
            destinationInError === '' && setDestinationInError(dateErrors.destinationInError);
            isError = true;
        } else {
            destinationInError !== '' && setDestinationInError('');
        }

        if (destinationOut && !isValidDate(new Date(destinationOut))) {
            destinationOutError === '' && setDestinationOutError(dateErrors.invalidError);
            isError = true;
        } else if (destinationOut && destinationIn && isValidDate(new Date(destinationIn)) && (moment(destinationOut) < moment(destinationIn))) {
            destinationOutError === '' && setDestinationOutError(dateErrors.destinationOutError);
            isError = true;
        } else if (status === "Delivered" && !destinationOut) {
            destinationOutError === '' && setDestinationOutError(dateErrors.dateRequiredError);
            isError = true;
        } else {
            destinationOutError !== '' && setDestinationOutError('');
        }

        return isError;
    }

    return (
        <Modal show={showModal} onHide={onClickCancel} dialogClassName="EquipmentProgressModal-Container">
            <Modal.Header className="EquipmentProgressModal-Header">
                <div className='EquipmentProgressModal-Header-Text'>
                    Edit Load Progress
                </div>
                <i className="material-icons EquipmentProgressModal-Close-Icon" onClick={onClickCancel}>
                    clear
                </i>
            </Modal.Header>
            <Modal.Body>
                {
                    error &&
                    <div className="EquipmentProgressModal-ErrorContainer">
                        {error}
                    </div>
                }
                <div className="EquipmentProgressModal-Body">
                    {loading &&
                        <div className="EquipmentProgressModal-Spinner">
                            <Loading />
                        </div>
                    }
                    <div className="EquipmentProgressModal-BodyLeftDetail">
                        <div className="EquipmentProgressModal-Dropdown">
                            <SearchableDropdown
                                dropdownOptions={loadStatusOptions}
                                id="equipmentType"
                                label="LOAD STATUS"
                                onSelect={value => { value && handleOnChangeStatus(parseInt(value)) }}
                                value={status}
                            />
                        </div>
                    </div>

                    <div className="EquipmentProgressModal-BodyRightDetail">
                        <div className="EquipmentProgressModal-Body-Title">At Origin (CST)</div>
                        <Row>
                            <Col className="EquipmentProgressModal-DateTimePicker">
                                <DateTimePicker
                                    label="DATE IN"
                                    value={originIn}
                                    error={originInError}
                                    onChange={(value: any) => handleDateInputChange('originIn', value)}
                                />
                            </Col>
                            <Col className="EquipmentProgressModal-DateTimePicker-Seperator">
                                <DateTimePicker
                                    label="DATE OUT"
                                    value={originOut}
                                    error={originOutError}
                                    onChange={(value: any) => handleDateInputChange('originOut', value)}
                                />
                            </Col>
                        </Row>
                        <div className="EquipmentProgressModal-Body-Title">At Destination (CST)</div>
                        <Row>
                            <Col className="EquipmentProgressModal-DateTimePicker">
                                <DateTimePicker
                                    label="DATE IN"
                                    value={destinationIn}
                                    error={destinationInError}
                                    onChange={(value: any) => handleDateInputChange('destinationIn', value)}
                                    required
                                />
                            </Col>
                            <Col className="EquipmentProgressModal-DateTimePicker-Seperator">
                                <DateTimePicker
                                    label="DATE OUT"
                                    value={destinationOut}
                                    error={destinationOutError}
                                    onChange={(value: any) => handleDateInputChange('destinationOut', value)}
                                />
                            </Col>
                        </Row>
                    </div>

                </div>
            </Modal.Body>

            <Modal.Footer className="EquipmentProgressModal-Button-Container">
                <div className="EquipmentProgressModal-Button">
                    <CustomButton title='Cancel' onClick={onClickCancel} isBordered />
                </div>
                <div className="EquipmentProgressModal-Button">
                    <CustomButton title='Save' onClick={handleOnClickSave} isDisabled={isSaveDisabled() || loading} />
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default EditEquipmentProgressModal;

